/**
 * Bottom-nav tab inventory — the pill/dock tab descriptors and the rules for
 * which tabs a role sees. Extracted from BottomTabBar.js (which was already at
 * the 500-line cap) so the inventory is a pure, unit-testable module and the
 * component stays presentational.
 *
 * DYNAMIC 5th SLOT (reflect-only, 2026-07-29)
 * ------------------------------------------
 * The bar carries at most 5 pills, but admin has 14 nav features, billing 9 and
 * platform 8. On every OVERFLOW feature (customers, reports, catalog, …) no pill
 * key matched `activeTab`, so the bar rendered fully unlit — and on tablet the
 * tinted dock indicator unmounted entirely (BottomTabBar measures
 * `[data-active-dock-tab]`, which didn't exist). The bar said "you are nowhere"
 * on 9 of admin's 14 screens.
 *
 * Fix: the LAST pill is a dynamic slot. When `activeTab` is an overflow feature,
 * that slot borrows the feature's identity (icon + short label) so the bar is
 * always lit; when it isn't, the slot reverts to its fixed default. This is
 * REFLECT-ONLY BY DESIGN — there is deliberately no persistence and no
 * "remember the last one". The slot only ever changes as a consequence of a
 * navigation the user just made, so it can never surprise them on a cold start
 * (founder decision, 2026-07-29 — do not add memory without re-deciding it).
 *
 * Two invariants the rest of the bar depends on:
 *  - The slot REPLACES the last tab, never appends. Tab COUNT is constant, so
 *    the other four never move under the user's finger.
 *  - The replacement carries `slotKey` (a stable React key). BottomTabBar keys
 *    its buttons by `slotKey || key` — without it, changing identity changes the
 *    React key, which UNMOUNTS/REMOUNTS the button and kills the chip's
 *    max-width/opacity morph on the one tab that is animating.
 */

import {
  LayoutDashboard,
  Briefcase,
  Calendar,
  Receipt,
  CircleUser,
  Building2,
  Users,
  Route,
  ClipboardList,
  MailPlus,
  Logs,
  Contact,
  Box,
  LineChart,
  History,
  Mail,
  Database,
  Gem,
  Sparkles,
  CalendarX,
} from 'lucide-react';

const DASHBOARD_TAB = { key: 'dashboard', icon: LayoutDashboard, labelId: 'mobileNav.panel', defaultLabel: 'Panel' };
const JOBS_TAB = { key: 'jobs', icon: Briefcase, labelId: 'mobileNav.jobs', defaultLabel: 'Trabajos' };
// RD-JOBS-002: worker/supervisor's 'jobs' tab IS their route board — surface
// it under the Route label on mobile. Different label per role, same key.
const WORKER_ROUTE_TAB = { key: 'jobs', icon: Route, labelId: 'mobileNav.myRoute', defaultLabel: 'Mi Ruta' };
const SUPERVISOR_ROUTE_TAB = { key: 'jobs', icon: Route, labelId: 'mobileNav.route', defaultLabel: 'Ruta' };
const ADMIN_ROUTE_TAB = { key: 'route', icon: Route, labelId: 'mobileNav.route', defaultLabel: 'Ruta' };
const SCHEDULE_TAB = { key: 'schedule', icon: Calendar, labelId: 'mobileNav.schedule', defaultLabel: 'Horario' };
// Worker's own schedule reads the SAME text as its sidebar item ("My Schedule" /
// "Mi Agenda", nav.mySchedule) — RD-NAV-008 one-name-per-tab. Supervisor keeps
// SCHEDULE_TAB, which already matches its sidebar ("Schedule" / "Horario").
const WORKER_SCHEDULE_TAB = { key: 'schedule', icon: Calendar, labelId: 'nav.mySchedule', defaultLabel: 'Mi Agenda' };
const ADMIN_TEAM_SCHEDULE_TAB = { key: 'team-schedule', icon: Calendar, labelId: 'mobileNav.teamSchedule', defaultLabel: 'Horario' };
const INVOICES_TAB = { key: 'invoices', icon: Receipt, labelId: 'mobileNav.invoices', defaultLabel: 'Facturas' };
// ClipboardList, NOT FilePen. `FilePen` is the app's DRAFT glyph — it means
// draft in invoiceStatusTokens / estimateStatusTokens / jobsBillingStatusTokens
// / jobInvoiceStatusMeta and is the Drafts segment pill. Estimates wearing it
// made one glyph mean two things in the same viewport (2026-08-15).
// KEEP IN SYNC with navigationSidebarNav.js — the sidebar and this bottom bar
// are two INDEPENDENT icon registrations for the same tab.
const ESTIMATES_TAB = { key: 'estimates', icon: ClipboardList, labelId: 'mobileNav.estimates', defaultLabel: 'Presupuestos' };
const ACCOUNT_TAB = { key: 'account', icon: CircleUser, labelId: 'mobileNav.account', defaultLabel: 'Cuenta' };
const CUSTOMERS_TAB = { key: 'customers', icon: Contact, labelId: 'mobileNav.customers', defaultLabel: 'Clientes' };
const TENANTS_TAB = { key: 'tenants', icon: Building2, labelId: 'mobileNav.tenants', defaultLabel: 'Companies' };
const PLATFORM_TEAM_TAB = { key: 'platform-users', icon: Users, labelId: 'mobileNav.platformTeam', defaultLabel: 'Team' };
const INVITE_ACTIVITY_TAB = { key: 'invite-activity', icon: MailPlus, labelId: 'mobileNav.inviteActivity', defaultLabel: 'Invites' };
// Platform pills (KAN-727, root AND super admin): Dashboard · Companies · Plans ·
// AI · Activity. Labels match the sidebar (nav.activity reads "Audit" — tabNamesCanon pins bar == sidebar).
const PLATFORM_PLANS_TAB = { key: 'plans', icon: Gem, labelId: 'mobileNav.plans', defaultLabel: 'Plans' };
const PLATFORM_AI_TAB = { key: 'ai-usage', icon: Sparkles, labelId: 'mobileNav.aiUsage', defaultLabel: 'AI' };
// KAN-992: the Insights page (internal key 'ai-hub') borrows the dynamic slot; it is never a fixed pill.
// Label = the sidebar's aiHub.navLabel text ("Insights" / "Análisis") — tabNamesCanon pins bar == sidebar.
const INSIGHTS_TAB = { key: 'ai-hub', icon: Sparkles, labelId: 'mobileNav.insights', defaultLabel: 'Insights' };
const PLATFORM_ACTIVITY_TAB = { key: 'activity', icon: Logs, labelId: 'mobileNav.audit', defaultLabel: 'Audit' };
// KAN-1022: the ROOT platform user's 5th pill. Audit moves to the ☰ drawer (overflow) for root only;
// icon = CalendarX (platform cancellations canon), label = the sidebar's nav.cancellations text.
const PLATFORM_CANCELLATIONS_TAB = { key: 'cancellations', icon: CalendarX, labelId: 'mobileNav.cancellations', defaultLabel: 'Cancellations' };

/** Stable React key for the dynamic slot — see the header note. */
export const DYNAMIC_SLOT_KEY = 'cvx-dynamic-slot';

/**
 * Overflow features the dynamic slot may borrow, keyed by the role's INTERNAL
 * tab key (what `useFeatureTabRouting` hands the shells — e.g. admin's
 * 'employees'/'branding', not the canonical 'team'/'company' URL features).
 *
 * Labels are the SHORT `mobileNav.*` family, not the drawer's `nav.*` — the
 * phone chip caps its label at 96px and ellipsizes past it.
 *
 * `ai-hub` (the Insights page, KAN-992) is an overflow feature for admin, billing and
 * supervisor: on /app/ai-hub the last pill borrows the Insights identity so the bar
 * is lit (supervisor's last fixed pill, Account, is borrowed while on Insights).
 * It is still never a FIXED pill. Worker/operations have no Insights page.
 */
const OVERFLOW_TABS = {
  admin: {
    customers: CUSTOMERS_TAB,
    catalog: { key: 'catalog', icon: Box, labelId: 'mobileNav.catalog', defaultLabel: 'Catálogo' },
    estimates: ESTIMATES_TAB,
    reports: { key: 'reports', icon: LineChart, labelId: 'mobileNav.reports', defaultLabel: 'Reportes' },
    'ai-hub': INSIGHTS_TAB,
    employees: { key: 'employees', icon: Users, labelId: 'mobileNav.team', defaultLabel: 'Equipo' },
    history: { key: 'history', icon: History, labelId: 'mobileNav.history', defaultLabel: 'Historial' },
    branding: { key: 'branding', icon: Building2, labelId: 'mobileNav.organization', defaultLabel: 'Company' },
    account: ACCOUNT_TAB,
  },
  billing: {
    reports: { key: 'reports', icon: LineChart, labelId: 'mobileNav.reports', defaultLabel: 'Reportes' },
    'ai-hub': INSIGHTS_TAB,
    communications: { key: 'communications', icon: Mail, labelId: 'mobileNav.communications', defaultLabel: 'Correos' },
    account: ACCOUNT_TAB,
  },
  supervisor: {
    'ai-hub': INSIGHTS_TAB,
  },
  operations: {
    account: ACCOUNT_TAB,
  },
  platform: {
    'platform-users': PLATFORM_TEAM_TAB,
    'invite-activity': INVITE_ACTIVITY_TAB,
    cancellations: PLATFORM_CANCELLATIONS_TAB,
    'data-cleanup': { key: 'data-cleanup', icon: Database, labelId: 'mobileNav.dataCleanup', defaultLabel: 'Limpieza' },
    account: ACCOUNT_TAB,
  },
  // worker runs 4 pills that already cover every feature it can reach (route
  // sub-pools normalize to 'jobs' via activeNavKey), so it has no overflow and
  // no dynamic slot. Supervisor's only overflow is Insights (KAN-992).
};

/**
 * Fixed pill inventory for a role. `hiddenNavKeys` filters platform tabs (e.g. a
 * non-root super-admin hides invite-activity). Insights is never a fixed pill (KAN-992).
 */
export function buildFixedTabs(role, hiddenNavKeys = [], isRootPlatform = false) {
  if (role === 'worker') {
    return [DASHBOARD_TAB, WORKER_ROUTE_TAB, WORKER_SCHEDULE_TAB, ACCOUNT_TAB];
  }
  if (role === 'supervisor') {
    return [DASHBOARD_TAB, SUPERVISOR_ROUTE_TAB, SCHEDULE_TAB, ACCOUNT_TAB];
  }
  if (role === 'billing') {
    // RD-NAV-013: Estimates is a fixed pill so it never gets borrowed away;
    // Customers holds the borrowed last slot and Account lives in ☰ (as admin
    // and operations).
    return [DASHBOARD_TAB, JOBS_TAB, INVOICES_TAB, ESTIMATES_TAB, CUSTOMERS_TAB];
  }
  if (role === 'operations') {
    // 5 tabs: dashboard, route, schedule, jobs, customers — Account moves to the
    // push-nav drawer, mirroring admin's pattern (admin also drops Account from
    // the bar to fit Route + Jobs + one more operational feature).
    // Canonical order (founder): schedule rides NEXT TO route, then work → people.
    return [DASHBOARD_TAB, ADMIN_ROUTE_TAB, ADMIN_TEAM_SCHEDULE_TAB, JOBS_TAB, CUSTOMERS_TAB];
  }
  if (role === 'platform') {
    // 5 pills. Root (KAN-1022, fail closed: only an explicit `true`) ends on
    // Cancellations; everyone else ends on Audit. Team, Invites (root only), Data
    // cleanup, Account (and Audit for root) live in the ☰ drawer and borrow the last slot when active.
    const tabs = [
      DASHBOARD_TAB,
      TENANTS_TAB,
      PLATFORM_PLANS_TAB,
      PLATFORM_AI_TAB,
      isRootPlatform === true ? PLATFORM_CANCELLATIONS_TAB : PLATFORM_ACTIVITY_TAB,
    ];
    if (!hiddenNavKeys.length) return tabs;
    return tabs.filter((t) => !hiddenNavKeys.includes(t.key));
  }
  // Admin: 5 daily tabs; overflow (customers, catalog, reports, Insights, etc.) in push-nav.
  // Order: WORK before MONEY (route → jobs → invoices), matching the drawer's
  // operations→financials grouping — the bar and drawer used to disagree.
  return [DASHBOARD_TAB, ADMIN_ROUTE_TAB, ADMIN_TEAM_SCHEDULE_TAB, JOBS_TAB, INVOICES_TAB];
}

/**
 * The overflow descriptor the dynamic slot should borrow for `activeTab`, or
 * null when the active tab is already a fixed pill (or the role has no
 * overflow). Pure — a function of exactly (role, activeTab, hiddenNavKeys,
 * isRootPlatform), which is why BottomTabBar's dock-indicator effect lists every
 * input it depends on (isRootPlatform rides in its resetKey, KAN-1022). Keep it
 * that way: any new input MUST join those deps or the tablet chip keeps a stale
 * measurement.
 */
export function resolveDynamicSlotTab(role, activeTab, hiddenNavKeys = [], isRootPlatform = false) {
  if (!activeTab) return null;
  const overflow = OVERFLOW_TABS[role];
  if (!overflow) return null;
  if (hiddenNavKeys.includes(activeTab)) return null;
  if (role === 'platform' && isRootPlatform === true) {
    // Root: Cancellations is a fixed pill (never borrowed); Audit is overflow.
    if (activeTab === 'cancellations') return null;
    if (activeTab === 'activity') return PLATFORM_ACTIVITY_TAB;
  }
  return overflow[activeTab] || null;
}

/**
 * Bottom pill inventory. When `activeTab` is an overflow feature the LAST pill
 * borrows its identity (constant tab count — the other pills never move).
 */
export function buildTabs(role, hiddenNavKeys = [], activeTab = null, isRootPlatform = false) {
  const tabs = buildFixedTabs(role, hiddenNavKeys, isRootPlatform);
  const dynamic = resolveDynamicSlotTab(role, activeTab, hiddenNavKeys, isRootPlatform);
  if (!dynamic || tabs.length === 0) return tabs;
  return [...tabs.slice(0, -1), { ...dynamic, slotKey: DYNAMIC_SLOT_KEY }];
}

/** Test-only view of the overflow registry (disjointness guard). */
export const __OVERFLOW_TABS_FOR_TEST = OVERFLOW_TABS;
